import { EventMap, EventNames, PayloadType } from "@/lib/events/types"    

type EventCallback<T> = (payload: T) => void | Promise<void>

interface SubscriptionOptions {
  priority?: number
}

interface EventBusOptions {
  debug?: boolean
  errorHandler?: (error: Error, eventName: string) => void
}

/**
 * A type-safe event bus implementation that handles event subscriptions and publications
 * @template TEventMap The map of event names to their payload types
 */
export class TypedEventBus<TEventMap extends EventMap> {
  private subscribers: Map<
    EventNames<TEventMap>,
    Array<{
      callback: EventCallback<any>
      priority: number
    }>
  > = new Map()

  private persistentEvents: Set<EventNames<TEventMap>> = new Set()
  private persistFunction?: (state: any) => void
  private debug: boolean
  private errorHandler: (error: Error, eventName: string) => void

  constructor(options: EventBusOptions = {}) {
    this.debug = options.debug ?? false
    this.errorHandler = options.errorHandler ?? this.defaultErrorHandler
  }

  private defaultErrorHandler(error: Error, eventName: string): void {
  }

  /**
   * Subscribe to an event
   * @param eventName The name of the event to subscribe to
   * @param callback The callback to be called when the event is published
   * @param options Subscription options including priority
   * @returns A function to unsubscribe from the event
   */
  subscribe<TEventName extends EventNames<TEventMap>>(
    eventName: TEventName,
    callback: EventCallback<TEventMap[TEventName]>,
    options: SubscriptionOptions = {}
  ): () => void {
    const subscribers = this.subscribers.get(eventName) || []
    const subscription = {
      callback,
      priority: options.priority ?? 0
    }

    subscribers.push(subscription)
    subscribers.sort((a, b) => b.priority - a.priority)
    this.subscribers.set(eventName, subscribers)

    if (this.debug) {
    }

    return () => {
      const subscribers = this.subscribers.get(eventName)
      if (subscribers) {
        const index = subscribers.findIndex(s => s.callback === callback)
        if (index !== -1) {
          subscribers.splice(index, 1)
          if (subscribers.length === 0) {
            this.subscribers.delete(eventName)
          }
          if (this.debug) {
          }
        }
      }
    }
  }

  /**
   * Publish an event with payload
   * @param eventName The name of the event to publish
   * @param data The data to be included in the event payload
   */
  async publish<TEventName extends EventNames<TEventMap>>(
    eventName: TEventName,
    data: PayloadType<TEventMap, TEventName>,
    source: string = 'system',
    metadata?: Record<string, unknown>
  ): Promise<void> {
    const subscribers = this.subscribers.get(eventName)
    if (!subscribers?.length) {
      if (this.debug) {
      }
      return
    }

    const payload = {
      data,
      timestamp: Date.now(),
      source,
      metadata,
      canceled: false,
      eventName: eventName as string
    }

    if (this.debug) {
    }

    for (const { callback } of subscribers) {
      if (payload.canceled) {
        if (this.debug) {
        }
        break
      }

      try {
        const result = callback(payload)
        if (result instanceof Promise) {
          await result
        }
      } catch (error) {
        this.errorHandler(error as Error, String(eventName))
      }
    }

    if (this.persistentEvents.has(eventName) && this.persistFunction) {
      try {
        this.persistFunction({ eventName, payload })
      } catch (error) {
        this.errorHandler(
          new Error(`Failed to persist event state: ${error}`),
          String(eventName)
        )
      }
    }
  }

  /**
   * Register events that should be persisted
   * @param eventNames Array of event names to persist
   * @param persistFunction Optional custom persist function
   */
  registerPersistentEvents(
    eventNames: EventNames<TEventMap>[],
    persistFunction?: (state: any) => void
  ): void {
    eventNames.forEach(eventName => this.persistentEvents.add(eventName))
    if (persistFunction) {
      this.persistFunction = persistFunction
    } else {
      this.persistFunction = (state: any) => {
        try {
          localStorage.setItem(
            `eventbus_${String(state.eventName)}`,
            JSON.stringify(state)
          )
        } catch (error) {
        }
      }
    }
  }

  /**
   * Get the number of subscribers for an event
   * @param eventName The name of the event
   * @returns The number of subscribers
   */
  getSubscriberCount<TEventName extends EventNames<TEventMap>>(
    eventName: TEventName
  ): number {
    return this.subscribers.get(eventName)?.length || 0
  }

  /**
   * Clear all subscriptions
   */
  clear(): void {
    this.subscribers.clear()
    this.persistentEvents.clear()
  }
}

/**
 * Create a singleton instance of the event bus
 */
export const createEventBus = <TEventMap extends EventMap>(
  options?: EventBusOptions
) => {
  return new TypedEventBus<TEventMap>(options)
} 